import React, { memo } from "react";
import { View, Text, StyleSheet, Svg, Polyline, Rect, Circle, Polygon } from "@react-pdf/renderer";

interface DataPoint { label: string; value: number; }

interface PDFUnemploymentRateChartProps {
    data: DataPoint[];
    stateData?: DataPoint[];
    nationalData?: DataPoint[];
    lineColor: string;
    title?: string;
    height?: number;
    icon?: React.FC | null;
}

const styles = StyleSheet.create({
    container: { width: '100%', marginTop: 8, marginLeft: 12 },
    chartArea: { width: 260, height: 120 },
    legend: { flexDirection: 'row', justifyContent: 'flex-start', marginTop: 4 },
    legendItem: { flexDirection: 'row', alignItems: 'center', marginRight: 8, fontSize: 8, color: '#3A2E29' },
    legendMarker: { width: 8, height: 8, marginRight: 4 },
    title: { fontSize: 10, marginBottom: 2, color: '#3A2E29', fontWeight: 700 },
    label: { fontSize: 8, color: '#3A2E29,', fontWeight: 400 },
    legendText: { color: '#3A2E29' }
});

interface Point {
    x: number;
    y: number;
}

const calculatePoints = (points: DataPoint[], width: number, height: number): Point[] => {
    if (!points || points.length === 0) return [];

    const values = points.map(p => p.value);
    const maxVal = Math.max(...values);
    const minVal = Math.min(...values);

    // Add padding to min/max to spread the data vertically
    const rangePadding = (maxVal - minVal) * 0.25 || 1; // fallback to 1 if range is 0
    const paddedMax = maxVal + rangePadding;
    const paddedMin = minVal - rangePadding;

    const valueRange = paddedMax - paddedMin;
    const stepX = width / (points.length - 1 || 1);

    return points.map((p, i) => ({
        x: i * stepX,
        y: height - ((p.value - paddedMin) / valueRange) * height,
    }));
};

const pointsToString = (pts: Point[]) => pts.map(p => `${p.x},${p.y}`).join(" ");

const formatPercent = (value?: number) =>
    value === undefined ? '' : `${(value <= 1 ? value * 100 : value).toFixed(1)}%`;

const PDFUnemploymentRateChart = memo(({ data, stateData, nationalData, lineColor, title, icon }: PDFUnemploymentRateChartProps) => {
    const width = 260;
    const height = 120;

    const linePoints = calculatePoints(data, width, height);
    const statePoints = stateData ? calculatePoints(stateData, width, height) : [];
    const nationalPoints = nationalData ? calculatePoints(nationalData, width, height) : [];

    const line = pointsToString(linePoints);
    const stateLine = pointsToString(statePoints);
    const nationalLine = pointsToString(nationalPoints);

    const selectedLast = data[data.length - 1]?.value;
    const stateLast = stateData && stateData[stateData.length - 1]?.value;
    const nationalLast = nationalData && nationalData[nationalData.length - 1]?.value;

    const lastPoint = linePoints[linePoints.length - 1];
    const lastLabel = data[data.length - 1]?.label;

    return (
        <View style={styles.container}>
            {icon && React.createElement(icon)}
            {title && <Text style={styles.title}>{title}</Text>}
            {lastPoint && lastLabel && (
                <Text style={{ ...styles.label, marginLeft: lastPoint.x }}>
                    {lastLabel}
                </Text>
            )}
            <Svg style={styles.chartArea}>
                {nationalLine && <Polyline points={nationalLine} fill="none" stroke="#125687" strokeWidth={2} />}
                {stateLine && <Polyline points={stateLine} fill="none" stroke="#C1BB00" strokeWidth={2} />}
                {line && <Polyline points={line} fill="none" stroke={lineColor} strokeWidth={2} />}

                {linePoints.map((p, i) => (
                    <Rect key={`s-${i}`} x={p.x - 3} y={p.y - 3} width={6} height={6} fill={lineColor} />
                ))}
                {statePoints.map((p, i) => (
                    <Polygon
                        key={`st-${i}`}
                        points={`${p.x},${p.y - 3} ${p.x - 3},${p.y + 3} ${p.x + 3},${p.y + 3}`}
                        fill="#C1BB00"
                    />
                ))}
                {nationalPoints.map((p, i) => (
                    <Circle key={`n-${i}`} cx={p.x} cy={p.y} r={3} fill="#125687" />
                ))}

                {/* Vertical line and label for last data point */}
                {lastPoint && lastLabel && (
                    <>
                        <Polyline
                            points={`${lastPoint.x},0 ${lastPoint.x},${height}`}
                            stroke="#E8E8E8"
                            strokeWidth={3}
                        />
                    </>
                )}
            </Svg>
            <View style={styles.legend}>
                {line && (
                    <View style={styles.legendItem}>
                        <Svg style={styles.legendMarker}>
                            <Rect width={8} height={8} fill={lineColor} />
                        </Svg>
                        <Text style={styles.legendText}>Selected: {formatPercent(selectedLast)}</Text>
                    </View>
                )}
                {stateLine && (
                    <View style={styles.legendItem}>
                        <Svg style={styles.legendMarker}>
                            <Polygon points="4,0 8,8 0,8" fill="#C1BB00" />
                        </Svg>
                        <Text style={styles.legendText}>State: {formatPercent(stateLast)}</Text>
                    </View>
                )}
                {nationalLine && (
                    <View style={styles.legendItem}>
                        <Svg style={styles.legendMarker}>
                            <Circle cx={4} cy={4} r={4} fill="#125687" />
                        </Svg>
                        <Text style={styles.legendText}>Country: {formatPercent(nationalLast)}</Text>
                    </View>
                )}
            </View>
        </View>
    );
});

export default PDFUnemploymentRateChart;